import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { Replay as ReplayIcon, StopCircle as StopIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import {
  useReleaseCheckStatus,
  useStartReleaseCheckCategoryRun,
  useStartReleaseCheckRun,
} from '../../api/release-check-api'
import { SUB_ORCHESTRATED_CATEGORIES } from '../../types/RunStatus'
import { CheckCategory } from '../../types/ReleaseCheckItem'
import CategoryRow, { isRunningState } from './CategoryRow'

const StatusHeader = () => {
  const { t } = useTranslation()
  const [status] = useReleaseCheckStatus()
  const [startRun, isStartingAll] = useStartReleaseCheckRun()
  const [startCategory, isStartingCategory, startingCategory] = useStartReleaseCheckCategoryRun()

  const umbrellaRunning = isRunningState(status?.state)
  const anyCategoryRunning = SUB_ORCHESTRATED_CATEGORIES.some((c) =>
    isRunningState(status?.categories?.[c]?.state),
  )
  const anythingRunning = umbrellaRunning || anyCategoryRunning

  const fullScanDisabled = anythingRunning || isStartingAll

  const confirmAndForceAll = () => {
    if (!window.confirm(t('releaseCheck.confirmRestart.bodyAll', 'This will terminate every in-flight scan and start a fresh full scan. Continue?'))) return
    void startRun({ force: true })
  }

  const confirmAndForceCategory = (category: CheckCategory) => {
    if (!window.confirm(t('releaseCheck.confirmRestart.bodyCategory', 'This will terminate the running scan for this category and start a fresh one. Continue?'))) return
    void startCategory({ category, force: true })
  }

  return (
    <Card sx={{ border: 1, borderColor: 'divider', mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={3} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t('releaseCheck.lastScan', 'Last scan')}
            </Typography>
            <Typography variant="h6">
              {status?.lastFinalizedAt
                ? new Date(status.lastFinalizedAt).toLocaleString()
                : t('releaseCheck.neverScanned', 'Never scanned')}
            </Typography>
            <Chip
              label={t(`releaseCheck.state.${status?.state ?? 'Idle'}`, status?.state ?? 'Idle')}
              size="small"
              color={umbrellaRunning ? 'warning' : 'default'}
              sx={{ mt: 1 }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              disabled={fullScanDisabled}
              onClick={() => void startRun({})}
            >
              {t('releaseCheck.rescanAll', 'Re-scan all')}
            </Button>
            {anythingRunning && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                disabled={isStartingAll}
                onClick={confirmAndForceAll}
              >
                {t('releaseCheck.stopAndRestart', 'Stop & restart')}
              </Button>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {SUB_ORCHESTRATED_CATEGORIES.map((category) => (
            <CategoryRow
              key={category}
              category={category}
              status={status?.categories?.[category]}
              umbrellaRunning={umbrellaRunning}
              onRescan={(c) => void startCategory({ category: c })}
              onForceRestart={confirmAndForceCategory}
              isStarting={isStartingCategory}
              startingCategory={startingCategory}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default StatusHeader
